import React, { useEffect, useState } from 'react';
import { Box, Typography, Slide } from '@mui/material';
import GuestTestKitActivationForm from './GuestTestKitActivationForm';
import CustomStepper from '../../common/CustomStepper';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { TestKitActivationLastSteps, TestKitGuestUserRegistration } from '../../../types/user';
import SignupStep1AccountDetails from '../../user/SignupStep1AccountDetails';
import SignupStep2ProfileInfo from '../../user/SignupStep2ProfileInfo';
import { RequestOptions } from '../../../types/httpRequests';
import useBackendApi from '../../../hooks/useBackendApi';
import { addIntCodeToPhoneNumber, getInitialUserThemeValue, sendConfirmationCode } from '../../../utils/user';
import { signIn } from 'aws-amplify/auth';
import SuccessfulActivationOrRegistration from './SuccessfulActivationOrRegistration';
import { AUTHENTICATED } from '../../../constants/user';
import useSnackBarAlertMessages from '../../../hooks/useSnackBarAlertMessages';
import { useAuthenticator } from '@aws-amplify/ui-react';
import { CustomDialog } from '../../common/CustomDialog';
import { useThemeContext } from '../../../contexts/BaseThemeContextProvider';
import SignupStep3Confirmation from '../../user/SignupStep3Confirmation';
import { useRegistration } from '../../../hooks/useRegistration';
import { TestKitActivationFormProps } from '../MainTestKitActivation';

const backendURL = process.env.REACT_APP_BACKEND_URL;

type StepNumbersProps = {
  [key: string]: number;
};

type InitialStepValues = {
  email: string;
  firstName: string;
  lastName: string;
};

export default function GuestTestKitActivation({ disableTestKitActivation }: TestKitActivationFormProps) {
  const { theme } = useThemeContext();
  const [testKitStep, setTestKitStep] = useState('testKitAccountValidation');
  const [userDetails, setUserDetails] = useState<TestKitGuestUserRegistration>({} as TestKitGuestUserRegistration);
  const [submitAndFinalize, setSubmitAndFinalize] = useState<boolean>(false);
  const [isSendingCode, setIsSendingCode] = useState(false);
  const [finalStepsData, setFinalStepsData] = useState<TestKitActivationLastSteps>({} as TestKitActivationLastSteps);
  const { apiFetchRequest } = useBackendApi();
  const { addAlertMessages, renderSnackBarAlertComponent } = useSnackBarAlertMessages();
  const { authStatus } = useAuthenticator((context) => [context.authStatus]);
  const [isAuthStatusReady, setIsAuthStatusReady] = useState(false);
  const navigate = useNavigate();
  let [searchParams] = useSearchParams();
  const activationCodeValue = searchParams.get('activationCode') || '';
  const { fetchAppointmentDetails, fetchClientBookingDetails } = useRegistration();

  const [errorMsgDescription, setErrorMsgDescription] = useState('');
  const [errorMsgTitle, setErrorMsgTitle] = useState('');
  const [isErrorDialogOpen, setIsErrorDialogOpen] = useState(false);

  useEffect(() => {
    if (authStatus !== 'configuring') {
      setIsAuthStatusReady(true);
    }

    if (!isAuthStatusReady) {
      return;
    }

    if (authStatus === AUTHENTICATED && testKitStep === 'testKitAccountValidation') return navigate('/');
  }, [authStatus, isAuthStatusReady, navigate, testKitStep]);

  const stepNumbers: StepNumbersProps = {
    testKitAccountValidation: 1,
    createYourAccount: 2,
    completeYourProfile: 3,
    confirmYourEmail: 4,
    successfulRegistrationAndActivation: 5,
  };

  const getStepNumber = (step: string) => {
    return stepNumbers[step];
  };

  const renderTitleStepper = () => {
    if (testKitStep === 'successfulRegistrationAndActivation') return;

    return (
      <>
        <Box sx={{ mb: 3 }}>
          <Link
            to="/"
            style={{
              color: theme.custom.components.guestTestKitActivation.headerStepper.backOption.color,
              textDecoration: 'none',
            }}
          >
            {'<'} Back
          </Link>
        </Box>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'flex-start',
          }}
        >
          <img
            style={{
              marginRight: '6px',
              marginTop: '9px',
            }}
            src={theme.custom.components.guestTestKitActivation.headerStepper.image.src}
            alt="exclamation"
          />
          <Typography
            sx={{
              fontSize: '15px',
              mt: 1,
              mb: 2,
            }}
            color={theme.custom.components.guestTestKitActivation.headerStepper.title.color}
            align="left"
          >
            Creating an account allows us to keep your test data completely secure. If you are participating in a
            clinical trial, your results may not be available in the app, or may be delayed until the end of the trial.
            Please contact the trial organizer for more details.
          </Typography>
        </Box>
      </>
    );
  };

  const renderStepperHeaders = () => {
    return (
      <Box sx={{ mb: 3, mt: 3, padding: '0 28px' }}>
        {renderTitleStepper()}
        <CustomStepper steps={5} activeStep={getStepNumber(testKitStep)}></CustomStepper>
      </Box>
    );
  };

  const submitSignUpStep1AccountDetails = async (values: any) => {
    setUserDetails({
      ...userDetails,
      first_name: values.firstName,
      family_name: values.lastName,
      tos_flag: values.tos_flag,
      marketing_flag: values.marketing_flag,
      sms_flag: values.sms_flag,
      hipaa_npp_flag: values.hipaa_npp_flag,
    });
    setTestKitStep('completeYourProfile');
  };

  const renderCreateYourAccount = () => {
    const disabledFields: (keyof InitialStepValues)[] = ['email'];

    if (userDetails.first_name) {
      disabledFields.push('firstName');
    }
    if (userDetails.family_name) {
      disabledFields.push('lastName');
    }

    return (
      <Box
        sx={{
          paddingLeft: '28px',
          [theme.breakpoints.down('md')]: {
            paddingBottom: '26px',
          },
          [theme.breakpoints.up('md')]: {
            paddingBottom: '10px',
          },
        }}
      >
        <SignupStep1AccountDetails
          cancelButtonText="Cancel activation"
          onSubmit={submitSignUpStep1AccountDetails}
          isSubmitting={false}
          initialFormValues={{
            email: userDetails.email,
            firstName: userDetails.first_name,
            lastName: userDetails.family_name,
            tos_flag: false,
            marketing_flag: false,
            sms_flag: false,
            hipaa_npp_flag: false,
            hipaa_copy_req_flag: false,
          }}
          disabledFields={disabledFields}
          fetchAppointmentDetails={fetchAppointmentDetails}
          fetchClientBookingDetails={fetchClientBookingDetails}
        />
      </Box>
    );
  };

  const completeYourProfileSubmit = async (values: any) => {
    setIsSendingCode(true);
    const updatedDetails = {
      ...userDetails,
      ...values,
      phone_number: values.phoneNumber,
    };
    setUserDetails(updatedDetails);

    try {
      const scc_r = await sendConfirmationCode(
        {
          email: updatedDetails.email,
          phone: updatedDetails.phoneNumber,
          sms_flag: updatedDetails.sms_flag ? '1' : '0',
        },
        apiFetchRequest,
      );
      setTestKitStep('confirmYourEmail');
    } catch (e) {
      addAlertMessages({
        message: 'Failed to send confirmation code. Please try again.',
        severityType: 'error',
      });
    } finally {
      setIsSendingCode(false);
    }
  };

  const resendConfirmationCode = async (setResendSubmitting: any, setMessage: any) => {
    setResendSubmitting(true);
    try {
      const scc_r = await sendConfirmationCode(
        {
          email: userDetails.email,
          phone: userDetails.phone_number,
          sms_flag: userDetails.sms_flag ? '1' : '0',
        },
        apiFetchRequest,
      );
      setMessage({
        text: 'Confirmation code resent.',
        severity: 'info',
      });
    } catch (signUpError: any) {
      setMessage({
        text: 'Error resending confirmation code:',
        severity: 'error',
      });
    } finally {
      // just to avoid spamming the button
      setTimeout(() => {
        setResendSubmitting(false);
      }, 4000);
    }
  };

  const submitConfirmationCode = async (values: any, { setSubmitting }: any) => {
    setSubmitAndFinalize(true);
    const allFields = { ...userDetails, ...values };
    const encodedActivationCode = encodeURIComponent(allFields.activationCode);
    const testKitActivationUrl = `${backendURL}/test_kits/${encodedActivationCode}/activate`;

    const customHeaders = {
      'x-user-id': allFields.x_user_id,
    };

    const phoneNumber = addIntCodeToPhoneNumber(allFields.phoneNumber);
    const currentTime = new Date().toISOString();

    const bodyPayload = {
      first_name: allFields.first_name,
      family_name: allFields.family_name,
      email: allFields.email,
      dob: allFields.birthdate,
      tos_flag: allFields.tos_flag ? '1' : '0',
      marketing_flag: allFields.marketing_flag ? '1' : '0',
      marketing_date: allFields.marketing_flag ? currentTime : '',
      phone_number: phoneNumber,
      gender: allFields.gender,
      address_1: allFields.address_1,
      address_2: allFields.address_2,
      city: allFields.city,
      state: allFields.state,
      zipcode: allFields.zipcode,
      occupation: allFields.occupation,
      theme: getInitialUserThemeValue(),
      confirmation_code: values.confirmationCode,
      sms_flag: allFields.sms_flag ? '1' : '0',
      sms_date: allFields.sms_flag ? currentTime : '',
      hipaa_npp_flag: allFields.hipaa_npp_flag ? '1' : '0',
      hipaa_npp_date: allFields.hipaa_npp_flag ? currentTime : '',
    };

    const apiRequestOptions: RequestOptions = {
      method: 'PUT',
      endpoint: testKitActivationUrl,
      body: bodyPayload,
      headers: customHeaders,
    };

    try {
      const response = await apiFetchRequest(apiRequestOptions);

      if (response.status !== 200) {
        setErrorMsgDescription(response.body.error_description);
        setErrorMsgTitle(response.body.error_title);
        setIsErrorDialogOpen(true);
        return;
      }

      const finalStepsData = {
        shopifyOrderId: userDetails.s_order_id,
        activationCode: userDetails.activationCode,
      };
      setFinalStepsData(finalStepsData);

      await signIn({
        username: bodyPayload.email,
        password: response.body.password,
      });

      setTestKitStep('successfulRegistrationAndActivation');
    } catch (err) {
      addAlertMessages({
        message: 'An error happened during the test kit activation',
        severityType: 'error',
      });
      console.log('GuestTestKitActivation exception ', err);
    } finally {
      setSubmitAndFinalize(false);
      setSubmitting(false);
    }
  };

  const renderCompleteYourProfile = () => {
    return (
      <Box
        sx={{
          paddingLeft: '28px',
          [theme.breakpoints.down('md')]: {
            paddingBottom: '26px',
          },
          [theme.breakpoints.up('md')]: {
            paddingBottom: '10px',
          },
        }}
      >
        <SignupStep2ProfileInfo
          initialFormValues={{
            email: userDetails.email,
            firstName: userDetails.first_name,
            lastName: userDetails.family_name,
            birthdate: userDetails.birth_date,
            address_1: userDetails.address_1,
            address_2: userDetails.address_2,
            phone_number: userDetails.phone_number,
            city: userDetails.city,
            state: userDetails.state,
            zipcode: userDetails.zipcode,
          }}
          disabledFields={['birthdate']}
          onSubmit={completeYourProfileSubmit}
          isSubmitting={isSendingCode}
          btSubmitText="Finalize & activate"
          btnCancelText="Cancel activation"
        />
      </Box>
    );
  };

  const renderSuccessfulRegistrationAndActivation = () => (
    <Box sx={{ paddingLeft: '28px' }}>
      <SuccessfulActivationOrRegistration finalStepsData={finalStepsData} isGuest={true} />
    </Box>
  );

  const renderStepsWithSlider = () => (
    <Box sx={theme.custom.components.guestTestKitActivation.containerStyles}>
      {renderSnackBarAlertComponent()}
      {renderStepperHeaders()}
      <Slide
        direction="left"
        in={testKitStep === 'testKitAccountValidation'}
        mountOnEnter
        unmountOnExit
        timeout={{ enter: 600, exit: 100 }}
      >
        <div>
          <GuestTestKitActivationForm
            activationCodeValue={activationCodeValue}
            setUserDetails={setUserDetails}
            setTestKitStep={setTestKitStep}
            disableTestKitActivation={disableTestKitActivation}
          />
        </div>
      </Slide>

      <Slide
        direction="left"
        in={testKitStep === 'createYourAccount'}
        mountOnEnter
        unmountOnExit
        timeout={{ enter: 600, exit: 100 }}
      >
        <div>{renderCreateYourAccount()}</div>
      </Slide>

      <Slide
        direction="left"
        in={testKitStep === 'completeYourProfile'}
        mountOnEnter
        unmountOnExit
        timeout={{ enter: 600, exit: 100 }}
      >
        <div>{renderCompleteYourProfile()}</div>
      </Slide>

      <Slide
        direction="left"
        in={testKitStep === 'confirmYourEmail'}
        mountOnEnter
        unmountOnExit
        timeout={{ enter: 600, exit: 100 }}
      >
        <div>
          <Box sx={{ paddingLeft: '28px' }}>
            <SignupStep3Confirmation
              onSubmit={submitConfirmationCode}
              email={userDetails.email}
              isSubmitting={submitAndFinalize}
              onResendCode={resendConfirmationCode}
            />
          </Box>
        </div>
      </Slide>

      <Slide
        direction="left"
        in={testKitStep === 'successfulRegistrationAndActivation'}
        mountOnEnter
        unmountOnExit
        timeout={{ enter: 600, exit: 100 }}
      >
        <div>{renderSuccessfulRegistrationAndActivation()}</div>
      </Slide>
    </Box>
  );

  const handleCloseCustomDialog = () => {
    setIsErrorDialogOpen(false);
    setErrorMsgDescription('');
    setErrorMsgTitle('');
  };

  return (
    <>
      <CustomDialog
        title={errorMsgTitle}
        errorMessage={errorMsgDescription}
        isOpen={isErrorDialogOpen}
        onClose={handleCloseCustomDialog}
      />
      {renderStepsWithSlider()}
    </>
  );
}
